import { useState, useEffect, useRef } from "react";
import FlexxQuizResultPanel from "@/flexx/FlexxQuizResultPanel";
import { FlexxCertificate, openCertificatePrintView } from "@/flexx/FlexxCertificate";

const FLEXX_LOGO = "data:image/png;base64,"; // Reuse same base64 from flexx-beginner.jsx in production

// ─── BRAND ───────────────────────────────────────────────────────────────────
const B = {
  black: "#000000", blackDeep: "#060606", blackCard: "#0C0C0C",
  blackPanel: "#111111", blackBorder: "#1C1C1C", blackHover: "#161616",
  gold: "#C9A227", goldLight: "#E8B94F", goldDark: "#9A7A10",
  goldGlow: "rgba(201,162,39,0.12)", goldFaint: "rgba(201,162,39,0.06)",
  white: "#FFFFFF", whiteOff: "#EDE8DC", whiteGray: "#AAAAAA", whiteMuted: "#555555",
  cryptoAccent: "#C9A227", forexAccent: "#4A9EFF", commodityAccent: "#E8883A",
  commodityFaint: "rgba(232,136,58,0.06)", commodityGlow: "rgba(232,136,58,0.12)",
};

// ─── COMMODITIES / CFD / FUTURES TRACK ──────────────────────────────────────
const TRACKS = {
  commodities: {
    id: "commodities",
    label: "CFD · FUTURES · COMMODITIES",
    icon: "⬢",
    accent: B.commodityAccent,
    faint: B.commodityFaint,
    glow: B.commodityGlow,
    tagline: "Gold · Oil · BTC Perpetuals · Traditional Futures — The 24/7 Trader's Edge",
    credit: "This track synthesizes institutional-grade commodity trading principles with the 2026 reality of tokenized commodities and DEX perpetuals (Hyperliquid HIP-3). FLEXX Academy integrates SageMaster AI tooling (DeepSage sentiment, AI Grid, Helix) into every setup so traders execute with both macro context and automation. The March 2026 Hyperliquid oil event — $1.2B daily volume on a weekend while CME was closed — is the thesis behind this curriculum.",
    phases: [
      // ─── PHASE 1: GOLD ─────────────────────────────────────────────
      {
        id: "p1", label: "XAUUSD — 3 Ways To Trade Gold", icon: "01", weeks: "Week 1–2",
        desc: "Gold is the oldest safe-haven and the most-traded commodity on MT5. In 2026, you can trade it three ways: broker CFD, tokenized spot (XAUT), or perpetual futures on Binance/Bybit/Hyperliquid. Each has different mechanics, costs, and opportunities.",
        modules: [
          {
            id: "p1-1", title: "Gold's DNA — What Actually Moves The Price",
            duration: "25 min", tag: "MACRO",
            summary: "Gold isn't random. It's driven by four forces: the US Dollar (DXY), real interest rates, geopolitical risk, and central bank buying. Master these and you know WHY gold moves before you care HOW to trade it.",
            keyPoints: [
              "DXY UP → Gold typically DOWN (inverse correlation — usually strongest)",
              "Real rates UP (Treasury yields minus inflation) → Gold DOWN — opportunity cost of holding non-yielding metal",
              "Geopolitical conflict / war / banking crisis → Gold UP (safe-haven flow)",
              "Central bank buying (China, Russia, India) has been a multi-year structural bid",
              "FOMC announcements, CPI releases, NFP — these are the event windows where gold moves hardest",
            ],
            practice: "Pull up DXY and XAUUSD side-by-side on TradingView. Scroll back 6 months. Note every week DXY moved — did gold move inverse? You'll see the correlation isn't perfect but it's the strongest single driver.",
            resources: [
              { label: "World Gold Council — What Drives Gold", url: "https://www.gold.org", type: "📄 READ", tag: "FREE" },
              { label: "SageMaster DeepSage — Gold Sentiment Module", url: "#", type: "🤖 TOOL", tag: "BE ACCESS" },
            ],
          },
          {
            id: "p1-2", title: "Method 1 — Broker CFD via MT5 (The Classic Way)",
            duration: "30 min", tag: "EXECUTION",
            summary: "Trading XAUUSD as a CFD on MT5 is how most retail traders access gold. You're not buying real gold — you're trading a contract that tracks its price. Understand the costs (spread, swap, commission) and leverage risks before you touch the button.",
            keyPoints: [
              "Contract: XAUUSD — quoted per ounce, 100 oz per standard lot",
              "Spread: typically 0.15–0.40 on ECN accounts, wider during news",
              "Swap (overnight financing): charged daily if you hold past 5PM NY — triple swap on Wednesday",
              "Leverage: up to 500:1 on offshore brokers — this is DANGEROUS for untrained traders",
              "Market hours: Sunday 5PM NY to Friday 5PM NY with a daily 1-hour break",
              "Best execution window: London open (3AM NY) and NY open (8AM NY) — highest liquidity",
            ],
            practice: "Open MT5 demo. Place a 0.01 lot XAUUSD trade. Hold it overnight. Check the swap charge in the next day's statement. Feel what overnight costs actually look like before you risk real money.",
            resources: [
              { label: "NextGen Funding Prop Firm — Trade Gold With Their Capital", url: "https://nextgenfunding.io", type: "💰 PROP", tag: "BE PARTNER" },
            ],
          },
          {
            id: "p1-3", title: "Method 2 — Tokenized Gold (XAUT) On Crypto Exchanges",
            duration: "25 min", tag: "TOKENIZED",
            summary: "In 2026, you can buy gold that lives on a blockchain. Tether Gold (XAUT) is backed 1:1 by physical gold in Swiss vaults — 1 token = 1 troy ounce. Trade it 24/7 on OKX, Binance, Bybit, or Uniswap. No overnight swaps, no broker required.",
            keyPoints: [
              "XAUT is available on OKX, Binance, Bitfinex spot markets",
              "Tether launched XAUT on BNB Chain in March 2026 — expanded DeFi access",
              "No swap fees — you actually OWN the token (redeemable for physical gold)",
              "Trades 24/7 including weekends — massive edge during geopolitical events",
              "Trade-off: lower leverage than MT5, but no overnight financing cost",
              "Best for: swing traders holding 3–30 days who hate paying swap",
            ],
            practice: "Create an OKX or Binance account. Look up the XAUT/USDT chart. Compare its price to XAUUSD on MT5. Note any premium or discount — this gap is often tradable.",
            resources: [
              { label: "Tether Gold Official Documentation", url: "https://gold.tether.to", type: "📄 READ", tag: "FREE" },
            ],
          },
          {
            id: "p1-4", title: "Method 3 — Gold Perpetual Futures (50x Leverage, 24/7)",
            duration: "30 min", tag: "PERPETUAL",
            summary: "The newest way to trade gold: perpetual futures on crypto exchanges. Binance launched XAUUSDT perps on January 5–7, 2026 via their Abu Dhabi regulated entity. Bybit offers 4 ways. Hyperliquid has fully on-chain gold perps under HIP-3. Up to 50x leverage, settled in USDT.",
            keyPoints: [
              "Binance XAUUSDT perps: up to 50x leverage, 24/7, USDT-margined via Nest Exchange (Abu Dhabi)",
              "Bybit: TradFi perps, copy trading, spot XAUT/USDT, and XAUT/PAXG perps",
              "Hyperliquid HIP-3: fully on-chain gold perps, no KYC, self-custody — growing fast",
              "Funding rate replaces swap — paid every 8 hours, can be positive or negative",
              "When funding is very positive → longs are overcrowded, potential reversal signal",
              "Watch CoinGlass for liquidation heatmaps — shows where leverage clusters sit",
            ],
            practice: "Go to CoinGlass.com. Find the XAUUSDT liquidation heatmap. Identify the nearest large liquidation cluster above and below current price — these are high-probability price magnets.",
            resources: [
              { label: "CoinGlass Liquidation Heatmap", url: "https://www.coinglass.com/LiquidationData", type: "🔥 TOOL", tag: "FREE" },
              { label: "Hyperliquid HIP-3 Documentation", url: "https://hyperliquid.xyz", type: "📄 READ", tag: "FREE" },
            ],
          },
          {
            id: "p1-5", title: "The FLEXX Gold Setup — Key Levels + EMA + Macro Trigger",
            duration: "35 min", tag: "FLEXX SETUP",
            summary: "Our signature gold setup combines three filters: a key horizontal level (prior swing high/low), 200 EMA confluence on the 4-hour, and a macro catalyst (FOMC, CPI, geopolitical event). When all three align, the probability is high and the risk-reward is clean.",
            keyPoints: [
              "Filter 1: Price at a key weekly swing high or low — mark the last 6 months of major S/R",
              "Filter 2: Price pulling back to 200 EMA on the 4H chart — confirms trend direction",
              "Filter 3: A macro event within 48 hours that aligns with your bias (e.g., dovish FOMC = gold long)",
              "Entry: On rejection candle at the level (engulfing, pin bar, or SMC change of character)",
              "Stop: Beyond the level's structure — never arbitrary pip counts",
              "Target: 2R minimum, scale at 1R, move stop to breakeven after 1R reached",
            ],
            practice: "Go back 3 months on XAUUSD. Mark every time all 3 filters aligned. Count the win rate with a 2R target. You'll see why confluence matters.",
            resources: [
              { label: "SageMaster DeepSage — Live Gold Sentiment Feed", url: "#", type: "🤖 TOOL", tag: "BE ACCESS" },
            ],
          },
        ],
      },
      // ─── PHASE 2: OIL ──────────────────────────────────────────────
      {
        id: "p2", label: "OIL — WTI, Brent, And The 24/7 Revolution", icon: "02", weeks: "Week 3–4",
        desc: "Oil used to be the hardest commodity for retail to trade — CME futures required $10K+ margin and only trade weekday hours. In March 2026, everything changed. Hyperliquid's oil perpetual hit $1.2B in daily volume on a weekend during US-Iran tensions while CME was closed. This is the new frontier.",
        modules: [
          {
            id: "p2-1", title: "Oil's DNA — OPEC, EIA Wednesday, And Geopolitics",
            duration: "25 min", tag: "MACRO",
            summary: "Oil is the most politically charged commodity on Earth. Supply is controlled by OPEC+ (a cartel), demand tracks global GDP, and every war in the Middle East sends it flying. Four things move oil: OPEC decisions, EIA weekly inventory (Wednesdays 10:30 AM NY), USD strength, and geopolitical shocks.",
            keyPoints: [
              "OPEC+ meetings: production cuts = oil UP, production increases = oil DOWN",
              "EIA Crude Oil Inventory — every Wednesday 10:30 AM NY — massive 5-minute moves",
              "Strong USD → Oil DOWN (oil is priced in dollars)",
              "Middle East conflict (Iran, Israel, Saudi) → Oil UP, often violently",
              "US shale production is the new swing producer — Permian Basin data matters",
              "Seasonality: oil usually rallies spring into summer (driving season)",
            ],
            practice: "Set a calendar reminder for every Wednesday at 10:30 AM NY. Watch the EIA release live on TradingView with USOIL open. You'll see the kind of moves that can make or destroy a week in 90 seconds.",
            resources: [
              { label: "EIA Weekly Petroleum Status Report", url: "https://www.eia.gov/petroleum/supply/weekly/", type: "📄 READ", tag: "FREE" },
            ],
          },
          {
            id: "p2-2", title: "Method 1 — Oil CFD on MT5 (USOIL / UKOIL / BRENT)",
            duration: "25 min", tag: "EXECUTION",
            summary: "MT5 brokers offer oil CFDs as USOIL (WTI Crude) and UKOIL (Brent). These track the front-month futures contract and roll automatically every month. Understand rollover dates — they can cause gaps that blow out stops.",
            keyPoints: [
              "USOIL tracks WTI (Texas crude), UKOIL tracks Brent (North Sea) — Brent usually trades $3–8 premium",
              "Contract rollover happens around the 20th–25th of each month — check your broker's calendar",
              "On rollover day, the contract can gap — widen stops or close positions before rollover",
              "Spread is typically 3–6 cents, wider during news",
              "Overnight swap applies daily — oil CFDs are expensive to hold long-term",
            ],
            practice: "On your MT5, find the contract specs for USOIL. Note the rollover date for this month. Mark your calendar to avoid holding positions through it.",
            resources: [
              { label: "CME Oil Futures Specs", url: "https://www.cmegroup.com/markets/energy/crude-oil/light-sweet-crude.html", type: "📄 READ", tag: "FREE" },
            ],
          },
          {
            id: "p2-3", title: "Method 2 — Oil Perpetuals on OKX, Binance, Bybit (NEW 2026)",
            duration: "30 min", tag: "PERPETUAL",
            summary: "March 4, 2026: OKX launched crude oil and copper contracts. Binance followed with CLUSDT (WTI), BZUSDT (Brent), and NATGASUSDT — up to 100x leverage, 24/7, no physical delivery. Bybit launched CLUSDT at up to 50x. For the first time, retail traders can trade oil around the clock with small accounts.",
            keyPoints: [
              "OKX: crude oil + copper contracts launched March 4, 2026 — new 'Commodities' category",
              "Binance CLUSDT (WTI), BZUSDT (Brent), NATGASUSDT — up to 100x, USDT-margined, no physical delivery",
              "Bybit CLUSDT: up to 50x leverage — conservative but clean execution",
              "24/7 trading — you can trade oil on Saturday morning while CME is closed",
              "Funding rates apply every 8 hours — watch them during geopolitical events",
              "Warning: 100x leverage is a trap for most traders — use 3–10x maximum until profitable",
            ],
            practice: "Create accounts on OKX and Binance. Compare CLUSDT and USOIL charts. On a weekend, check if there's oil-related news — and see how the perp prices before MT5 opens Monday.",
            resources: [
              { label: "Binance Commodity Futures", url: "https://www.binance.com/en/futures", type: "🌐 TOOL", tag: "FREE" },
            ],
          },
          {
            id: "p2-4", title: "Method 3 — Hyperliquid CL-USDC (The $1.2B Weekend Story)",
            duration: "30 min", tag: "DEX · HIP-3",
            summary: "In March 2026, US-Iran tensions disrupted oil supply on a Saturday. Traditional markets were closed. CME was closed. But Hyperliquid's on-chain oil perpetual hit $1.2B in 24-hour volume — surpassing Ethereum to become the platform's second-most-traded market. This is why 24/7 on-chain commodities matter.",
            keyPoints: [
              "Hyperliquid HIP-3 framework: fully on-chain perpetuals, self-custody, no KYC",
              "CL-USDC oil perpetual grew 100x in volume in 6 months",
              "March 2026: $1.2B open interest, 7 of top 30 markets on Hyperliquid are now non-crypto",
              "Advantage: trade oil during geopolitical weekend events when CME is closed",
              "Disadvantage: requires wallet setup, gas knowledge, smaller ecosystem than CEX",
              "The lesson: when liquidity moves on-chain, so does opportunity",
            ],
            practice: "Set up a Hyperliquid account with a small funded wallet. Place one paper-tracked oil perp trade on a Saturday. Feel what it's like to trade a commodity when the TradFi world is asleep.",
            resources: [
              { label: "Hyperliquid Exchange", url: "https://hyperliquid.xyz", type: "🌐 DEX", tag: "FREE" },
              { label: "Bloomberg: Hyperliquid Oil Volume Story (March 2026)", url: "#", type: "📰 NEWS", tag: "ARCHIVE" },
            ],
          },
          {
            id: "p2-5", title: "The FLEXX Oil Setup — Range + Trend + News Filter",
            duration: "35 min", tag: "FLEXX SETUP",
            summary: "Oil has three distinct modes: ranging (70% of the time), trending (20%), news-driven (10%). Each mode needs a different strategy. Our framework teaches you to identify the mode FIRST, then apply the right playbook. Wrong tool in wrong mode = losses.",
            keyPoints: [
              "Range mode: use AI Grid from SageMaster — grid trades between support and resistance",
              "Trend mode: use trend-following on 4H — pullbacks to 50 EMA after fresh breakout",
              "News mode: stay out 15 min before/after EIA, FOMC, OPEC meetings — or trade the reaction, not the event",
              "Filter: ADX on 4H — below 20 = range, above 25 = trend",
              "Risk: never risk more than 1% per oil trade — it's more volatile than gold or forex",
              "Position sizing: halve normal size during geopolitical weeks",
            ],
            practice: "Pull up USOIL 4H chart. Add ADX(14). Go back 6 months. Classify each 2-week period as range/trend/news. You'll see how often the regime changes.",
            resources: [
              { label: "SageMaster AI Grid — Oil Range Bot", url: "#", type: "🤖 TOOL", tag: "BE ACCESS" },
            ],
          },
        ],
      },
      // ─── PHASE 3: BTC 24/7 ─────────────────────────────────────────
      {
        id: "p3", label: "BTCUSD — CFD, Perps & The 24/7 Edge", icon: "03", weeks: "Week 5–6",
        desc: "Bitcoin can be traded three ways: as a CFD on MT5, as spot on exchanges, or as a perpetual future on Binance/Bybit/Hyperliquid. The critical difference is cost and hours. Master all three modes and know when to use each.",
        modules: [
          {
            id: "p3-1", title: "The 3 Modes — CFD vs Spot vs Perp",
            duration: "25 min", tag: "FOUNDATION",
            summary: "BTCUSD on MT5 is a CFD — a contract that tracks price. BTC on Binance spot is actual Bitcoin. BTCUSDT perpetual is a derivative with funding rates and leverage. Each has different costs, hours, and use cases.",
            keyPoints: [
              "MT5 CFD: weekday hours only (closes weekends), overnight swap, familiar MT5 tooling",
              "Exchange spot: 24/7, no leverage by default, you own the actual BTC, capital-heavy",
              "Exchange perps: 24/7, up to 125x leverage, funding rate every 8 hours, USDT-margined",
              "Weekend gap risk: MT5 closes Friday 5PM NY, reopens Sunday — huge gap if news breaks on weekend",
              "This is why serious BTC traders hold positions on exchanges, not MT5",
            ],
            practice: "Compare the BTCUSD chart on MT5 to BTCUSDT on Binance. Look at weekend gaps on MT5 — those gaps didn't exist on the exchange. Weekend is where exchange traders get the edge.",
            resources: [
              { label: "Binance Futures Academy", url: "https://academy.binance.com", type: "📚 LEARN", tag: "FREE" },
            ],
          },
          {
            id: "p3-2", title: "Funding Rates — The Hidden Cost (And Signal)",
            duration: "25 min", tag: "PERPETUAL",
            summary: "Funding rate is a payment that flips between longs and shorts every 8 hours to keep perp price anchored to spot. High positive funding = longs pay shorts (longs are overcrowded). High negative funding = shorts pay longs. Use it as both cost and contrarian signal.",
            keyPoints: [
              "Funding rate is charged/paid every 8 hours (00:00, 08:00, 16:00 UTC on most exchanges)",
              "Positive funding (e.g., +0.05%) = longs pay shorts — bullish positioning is crowded",
              "Negative funding (e.g., -0.02%) = shorts pay longs — bearish positioning is crowded",
              "Extreme funding (+0.1% or more for multiple periods) = high reversal probability",
              "CoinGlass has a free funding rate dashboard across all major exchanges",
              "Pro move: when funding is extreme and price stalls, look for reversal with SMC entry",
            ],
            practice: "Go to CoinGlass funding rate page. Find a day where BTC funding was above +0.1% for 3+ periods. Check what BTC did next — you'll see how often extreme funding precedes pullbacks.",
            resources: [
              { label: "CoinGlass Funding Rate Dashboard", url: "https://www.coinglass.com/FundingRate", type: "🔥 TOOL", tag: "FREE" },
            ],
          },
          {
            id: "p3-3", title: "Sunday Gap Strategy — The Weekend Edge",
            duration: "30 min", tag: "FLEXX SETUP",
            summary: "BTC trades 24/7 on exchanges but MT5 closes weekends. When BTC moves significantly over the weekend, MT5 traders come in Monday to a gap. This gap has a statistical tendency to partially fill. This is one of the cleanest edges retail traders can exploit.",
            keyPoints: [
              "BTC moves 2%+ over weekend → MT5 gaps at Sunday 5PM NY reopen",
              "Statistically, 60–70% of weekend gaps partially fill within 48 hours",
              "Entry: Sunday evening (after gap opens) in direction of gap fill",
              "Stop: beyond the weekend high/low on exchange (not MT5)",
              "Target: 50% fill of the gap, then move to breakeven",
              "Best for: traders who can't watch charts 24/7 — you only need to show up Sunday evening",
            ],
            practice: "Track the last 10 weekend gaps on BTCUSD. Did they fill within 48 hours? Count the win rate. This is your statistical edge.",
            resources: [
              { label: "SageMaster Helix — 24/7 BTC Automation", url: "#", type: "🤖 TOOL", tag: "BE ACCESS" },
            ],
          },
          {
            id: "p3-4", title: "The FLEXX 24/7 Risk Protocol",
            duration: "25 min", tag: "RISK",
            summary: "Trading 24/7 sounds great until you realize your position can blow up at 3 AM while you're asleep. Our risk protocol cuts size during high-risk windows, uses auto-stops, and integrates SageMaster Helix for automated management. You sleep — the bot manages.",
            keyPoints: [
              "Reduce position size by 50% during weekends and overnight Asia session",
              "ALWAYS use hard stops on exchange — never mental stops",
              "Set alerts (not trades) for FOMC, CPI, major events at unusual hours",
              "SageMaster Helix can manage positions 24/7 — trail stops, take partial profits",
              "Rule: if you can't afford to wake up to the worst-case scenario, position is too large",
              "Max drawdown per day: 3% of account. Hit it? Stop for 24 hours. No exceptions.",
            ],
            practice: "Write out your personal 24/7 risk rules. Print them. Stick them next to your monitor. When you break them, you know exactly why you lost.",
            resources: [
              { label: "SageMaster Helix — 24/7 Position Manager", url: "#", type: "🤖 TOOL", tag: "BE ACCESS" },
            ],
          },
        ],
      },
      // ─── PHASE 4: TRADITIONAL FUTURES ──────────────────────────────
      {
        id: "p4", label: "Traditional Futures (CME) — ES, NQ, GC, CL, BTC", icon: "04", weeks: "Week 7–8",
        desc: "Traditional futures trade on CME Group and are the institutional standard. E-mini S&P (ES), Nasdaq (NQ), Gold (GC), Crude (CL), and BTC CME are the 5 contracts that matter. Higher capital required, but cleaner execution than CFDs.",
        modules: [
          {
            id: "p4-1", title: "What Are Futures? Contracts, Expiry, Rollover",
            duration: "25 min", tag: "FOUNDATION",
            summary: "A futures contract is an agreement to buy or sell an asset at a specific price on a specific future date. Each contract has an expiration — you either close before expiry or roll to the next contract. Futures are standardized, centrally cleared, and the institutional standard.",
            keyPoints: [
              "Standardized contract size (e.g., ES = $50 × S&P 500 index value)",
              "Expiration months: March (H), June (M), September (U), December (Z)",
              "Front month = nearest expiration with most volume",
              "Rollover: happens 7–8 days before expiry when volume shifts to next contract",
              "Margin requirements: set by exchange (e.g., ES day margin ~$500, overnight ~$13K)",
              "No overnight swap like CFDs — but overnight margin is much higher",
            ],
            practice: "Go to CME Group website. Find the specs for ES (E-mini S&P 500). Note contract size, tick value, trading hours. This is the contract institutions live and die by.",
            resources: [
              { label: "CME Group — Contract Specs", url: "https://www.cmegroup.com", type: "📄 READ", tag: "FREE" },
            ],
          },
          {
            id: "p4-2", title: "The 5 Futures Every Trader Should Know",
            duration: "30 min", tag: "PRODUCTS",
            summary: "Out of dozens of futures contracts, these 5 cover 90% of professional trading flow. Learn their tick values, margins, and personalities. Each has its own rhythm.",
            keyPoints: [
              "ES (E-mini S&P 500): $50/point, 23hr trading, smoothest liquidity — the 'default' index trade",
              "NQ (E-mini Nasdaq): $20/point, more volatile than ES, tech-heavy, bigger intraday ranges",
              "GC (Gold): $100/point, cleaner than XAUUSD CFD, lower spread cost",
              "CL (Crude Oil): $1000/point (HUGE), highly volatile, EIA Wednesday = chaos",
              "BTC CME futures: $5/point (micro), regulated alternative to crypto exchanges",
              "MES, MNQ, MGC, MCL = MICRO versions at 1/10 size — beginner-friendly",
            ],
            practice: "Calculate: if ES moves 10 points and you have 1 contract, P&L = $500. If MES (micro) moves 10 points, P&L = $50. Start with micros until you're consistently profitable.",
            resources: [
              { label: "CME Micro Futures Overview", url: "https://www.cmegroup.com/trading/equity-index/micro-e-mini-futures.html", type: "📄 READ", tag: "FREE" },
            ],
          },
          {
            id: "p4-3", title: "Futures vs CFD vs Perps — The Comparison",
            duration: "20 min", tag: "COMPARISON",
            summary: "Same underlying asset — gold, oil, indices — three different wrappers. The right wrapper depends on your capital, timeframe, and jurisdiction. This module breaks down exactly when to use which.",
            keyPoints: [
              "CFD: low capital, high leverage, overnight swap, broker-dependent spread — BEST for small accounts learning",
              "Futures: higher capital, regulated, no swap, clean execution — BEST for serious traders with $10K+ accounts",
              "Perps: 24/7, flexible size, funding rate — BEST for crypto-native traders wanting commodity exposure",
              "Tax treatment differs wildly by country — consult an accountant",
              "Psychology: CFDs feel 'gamblier', futures feel 'serious' — the markets don't care but your brain does",
            ],
            practice: "Take one asset (gold). Calculate the total cost of holding for 30 days in each wrapper: CFD (swap × 30), futures (margin opportunity cost), perps (funding × 90 periods). Which is cheapest for you?",
            resources: [],
          },
          {
            id: "p4-4", title: "Prop Firms — Apex, Topstep, NextGen",
            duration: "30 min", tag: "CAPITAL",
            summary: "Prop firms let you trade futures (and CFDs) with their capital after passing an evaluation. Apex Trader Funding and Topstep are the two biggest futures prop firms. NextGen Funding is the BE-partner firm for CFD traders. Done right, this is how you scale without risking personal capital.",
            keyPoints: [
              "Apex Trader Funding: $25K–$300K accounts, rule-based evaluation, profit splits 90/10 after payout",
              "Topstep: $50K–$150K accounts, longer evaluation, conservative rules, trusted brand",
              "NextGen Funding (BE partner): CFD-focused, integrates with MT5 — what many FLEXX members use",
              "Common rules: daily loss limit, overall drawdown, profit target, minimum trading days",
              "DON'T fail the eval by being greedy — most failures come from oversized positions on day 1",
              "Treat the evaluation like a real account — same discipline, same process",
            ],
            practice: "Pick one prop firm. Read their full rules document. Write a 1-page plan: how will you pass the evaluation without breaking any rule? This plan is worth more than any course.",
            resources: [
              { label: "NextGen Funding — FLEXX Partner Prop Firm", url: "https://nextgenfunding.io", type: "💰 PROP", tag: "BE PARTNER" },
              { label: "Apex Trader Funding", url: "https://apextraderfunding.com", type: "💰 PROP", tag: "FUTURES" },
              { label: "Topstep", url: "https://www.topstep.com", type: "💰 PROP", tag: "FUTURES" },
            ],
          },
          {
            id: "p4-5", title: "FLEXX On Futures — Same S.P.E.R., Different Execution",
            duration: "25 min", tag: "FLEXX SETUP",
            summary: "Our core trading framework (S.P.E.R. — Setup, Plan, Execute, Review) works identically on futures. What changes is execution: tick-based entries, exchange-driven order types, and different session rhythms. Master the framework on CFDs first, then port it to futures.",
            keyPoints: [
              "Setup: same multi-timeframe analysis — weekly bias, 4H structure, 15M/5M entry",
              "Plan: always pre-define entry, stop, target before the session opens",
              "Execute: use limit orders where possible, market orders only when volatility demands it",
              "Review: journal every trade — tag by setup type, session, and rule adherence",
              "Key difference: futures sessions have distinct personalities (Asia = quiet, London = volatile, NY = decisive)",
              "Best futures windows: 9:30–11:00 AM NY (cash open) and 2:00–4:00 PM NY (closing flow)",
            ],
            practice: "Take your 10 best CFD trades from the last month. Map each one to a futures contract (XAUUSD → GC, US30 → ES, BTCUSD → BTC CME). Could you have executed the same setup? Usually yes — and usually cheaper.",
            resources: [
              { label: "SageMaster Intelligent Backtest — Futures Module", url: "#", type: "🤖 TOOL", tag: "BE ACCESS" },
            ],
          },
        ],
      },
    ],
  },
};

// ─── COMPONENTS ──────────────────────────────────────────────────────────────
function GoldBar({ color = B.gold }) {
  return <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />;
}

function Tag({ text, color = B.gold, bg }) {
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px",
      fontSize: 8, fontFamily: "monospace", letterSpacing: 2,
      color, background: bg || `${color}15`,
      border: `1px solid ${color}40`, borderRadius: 2,
    }}>{text}</span>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
// ─── SAGEMASTER PRODUCT SUITE ─────────────────────────────────────────────────
const SAGEMASTER_PRODUCTS = [
  {
    id: "ai-screener",
    name: "AI Screener",
    icon: "🔍",
    tagline: "Find the best setups across 100+ assets in seconds",
    desc: "AI-powered scanner that filters the entire market to surface only high-probability setups matching your criteria. No more manually scanning charts one by one.",
    useCase: "Run every Sunday before the week opens. Filter by: EMA stack aligned + ADX > 25 + volume spike. This becomes your weekly FLEXX SCAN watchlist automatically.",
    url: "https://sagemaster.com",
    tag: "WEEKLY SCAN",
    color: "#4A9EFF",
  },
  {
    id: "deepsage",
    name: "DeepSage AI Analysis",
    icon: "🧠",
    tagline: "Institutional-grade AI market analysis on demand",
    desc: "Deep AI analysis engine that reads market structure, sentiment, and pattern data across multiple timeframes. Think of it as a junior analyst who never sleeps.",
    useCase: "Before entering any major trade, run DeepSage on the asset. Cross-reference its reading with your FLEXX checklist. If both align — high conviction. If they disagree — wait.",
    url: "https://sagemaster.com",
    tag: "PRE-TRADE",
    color: "#C9A227",
  },
  {
    id: "helix",
    name: "Helix",
    icon: "🌀",
    tagline: "Advanced pattern recognition + entry signal engine",
    desc: "Identifies complex chart patterns, FCS channels, and Elliott Wave structures automatically. Saves hours of manual charting work.",
    useCase: "Use Helix to validate your FCS channel entries. If Helix confirms the same E1/E2/E3 entry zone you identified manually, your confidence in the trade increases significantly.",
    url: "https://sagemaster.com",
    tag: "ENTRY CONFIRMATION",
    color: "#9B59B6",
  },
  {
    id: "ai-grid",
    name: "AI Grid",
    icon: "⚡",
    tagline: "Automated grid trading for ranging market conditions",
    desc: "Deploys intelligent grid strategies during sideways/ranging markets. While trend-following setups rest, AI Grid harvests the chop profitably.",
    useCase: "When ADX is below 20 (no trend), activate AI Grid on stable pairs like BTC/USDT or ETH/USDT. Set upper and lower bounds at key S/R levels. Let it work while you focus on learning.",
    url: "https://sagemaster.com",
    tag: "RANGING MARKETS",
    color: "#E67E22",
  },
  {
    id: "ai-dca",
    name: "AI DCA",
    icon: "📊",
    tagline: "Smart dollar-cost averaging with AI-optimized entry timing",
    desc: "Not all DCA is equal. AI DCA identifies statistically optimal entry points within your DCA schedule — buying more when conditions are most favorable, less when they're not.",
    useCase: "Perfect for long-term crypto positions (BTC, ETH). Set a monthly budget, let AI DCA distribute entries across the month at optimal price points instead of fixed calendar dates.",
    url: "https://sagemaster.com",
    tag: "LONG-TERM ACCUMULATION",
    color: "#2ECC71",
  },
  {
    id: "ai-omni",
    name: "AI Omni",
    icon: "🌐",
    tagline: "All-in-one multi-strategy AI trading system",
    desc: "The flagship tool. Combines screening, pattern recognition, entry signals, and risk management into a unified AI trading framework.",
    useCase: "Intermediate to advanced use. Once you've mastered the FLEXX manual system and understand WHY each signal fires, migrate your best setups into AI Omni for systematic execution.",
    url: "https://sagemaster.com",
    tag: "ADVANCED · FLAGSHIP",
    color: "#C9A227",
  },
  {
    id: "ai-strategy-pack",
    name: "AI Strategy Pack",
    icon: "📦",
    tagline: "Pre-built, battle-tested trading strategy templates",
    desc: "Library of ready-to-deploy trading strategies covering trend-following, momentum, mean-reversion, and breakout approaches — all validated with historical data.",
    useCase: "Start with the 'FLEXX EMA Stack' strategy template. Deploy it in Demo mode first. Run 50 trades. Check your hit ratio and edge ratio. Then decide if it's ready for live capital.",
    url: "https://sagemaster.com",
    tag: "STRATEGY TEMPLATES",
    color: "#4A9EFF",
  },
  {
    id: "intelligent-backtest",
    name: "Intelligent Backtest",
    icon: "📈",
    tagline: "Test any strategy against years of historical data instantly",
    desc: "Before putting real money on any strategy, backtest it across 1-5 years of historical data. See win rate, max drawdown, and expected returns before risking a cent.",
    useCase: "Rule: Never trade a strategy live that hasn't been backtested with minimum 100 trades. Use Intelligent Backtest to validate your FLEXX entries. 40%+ win rate + 2x edge = deployable.",
    url: "https://sagemaster.com",
    tag: "VALIDATION ESSENTIAL",
    color: "#E74C3C",
  },
  {
    id: "magic-terminal",
    name: "Magic Terminal",
    icon: "⌨️",
    tagline: "Professional order execution + trade management terminal",
    desc: "Execute trades with precision. Advanced order types, OCO orders, bracket orders, and automated trail stops. The professional's execution environment.",
    useCase: "Once your strategy is validated, use Magic Terminal for all live entries. Set your stop + target + trail stop in one click at entry time. This removes all emotional decision-making during the trade.",
    url: "https://sagemaster.com",
    tag: "LIVE EXECUTION",
    color: "#1ABC9C",
  },
];


const ONBOARDING_QUIZ = [
  {
    id: "q1",
    question: "How would you describe your trading experience right now?",
    options: [
      { label: "Total beginner — I've never placed a real trade", value: "beginner" },
      { label: "Some experience — I've dabbled but lost money or don't have a system", value: "beginner" },
      { label: "Intermediate — I understand charts and have a partial strategy", value: "intermediate" },
      { label: "Advanced — I have a strategy but want to refine it with AI/SMC tools", value: "intermediate" },
    ],
  },
  {
    id: "q2",
    question: "What's your primary market interest?",
    options: [
      { label: "Crypto (Bitcoin, Altcoins, DeFi)", value: "crypto" },
      { label: "Stocks & Indices (NASDAQ, S&P 500)", value: "crypto" },
      { label: "Forex (EUR/USD, GBP/USD, JPY pairs)", value: "forex" },
      { label: "I want to learn all markets", value: "crypto" },
    ],
  },
  {
    id: "q3",
    question: "What's your biggest challenge right now?",
    options: [
      { label: "I don't know where to start or what to study", value: "beginner" },
      { label: "I know some things but I lose money consistently", value: "beginner" },
      { label: "I have entries but can't hold trades or manage exits", value: "intermediate" },
      { label: "I want to add AI screening and algorithmic tools to my arsenal", value: "intermediate" },
    ],
  },
];



// ─── MODULE QUIZZES ───
const MODULE_QUIZZES = {
  "p1-1": { moduleTitle: "Gold Commodities Quiz", questions: [
    { q: "What drives gold prices most consistently?", options: ["Stock market","USD strength and real interest rates","Oil prices","Crypto markets"], correct: 1 },
    { q: "XAUUSD on MT5 is...", options: ["Physical gold purchase","A CFD tracking gold price","A futures contract","A gold stock"], correct: 1 },
    { q: "Gold typically moves _____ when USD rises", options: ["Up","Down","Sideways","Randomly"], correct: 1 },
    { q: "DeepSage helps traders by...", options: ["Executing trades automatically","Providing AI-powered market analysis","Managing their portfolio","Connecting to brokers"], correct: 1 },
  ]},
};

// ─── ALL MODULES (flat) ───
const ALL_COMM_MODS = TRACKS.commodities.phases.flatMap(ph =>
  ph.modules.map(m => ({ ...m, phaseId: ph.id, phaseLabel: ph.label }))
);

export default function FlexxCommodities() {
  const [view, setView] = useState("map"); // map | lesson | credit
  const [currentModule, setCurrentModule] = useState(null);

  // ── v5 features ──
  const [notes, setNotes] = useState({});
  const [noteInput, setNoteInput] = useState("");
  const [completed, setCompleted] = useState({});
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [onboardingAnswers, setOnboardingAnswers] = useState({});
  const [onboardingResult, setOnboardingResult] = useState(null);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [showCert, setShowCert] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [quizPassed, setQuizPassed] = useState({});
  const [streakData, setStreakData] = useState({ streak: 0, lastVisit: null, totalDays: 0 });
  const [sageMasterOpen, setSageMasterOpen] = useState(false);
  const [sageMasterProduct, setSageMasterProduct] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [lbName, setLbName] = useState("");
  const [lbSubmitted, setLbSubmitted] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [adminPwInput, setAdminPwInput] = useState("");
  const [adminPwError, setAdminPwError] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [videoUrls, setVideoUrls] = useState({});
  const [adminVideoEditing, setAdminVideoEditing] = useState(null);
  const [adminVideoInput, setAdminVideoInput] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncCode, setSyncCode] = useState("");
  const [syncImportInput, setSyncImportInput] = useState("");
  const [syncImportError, setSyncImportError] = useState("");
  const [syncImportSuccess, setSyncImportSuccess] = useState(false);
  const [threads, setThreads] = useState({});
  const [threadInput, setThreadInput] = useState("");
  const [threadAuthor, setThreadAuthor] = useState("");
  const [affiliateCode, setAffiliateCode] = useState("");


  const track = TRACKS.commodities;
  const accent = track.accent;

  const openLesson = (module) => {
    setCurrentModule(module);
    setView("lesson");
  };


  // ── Derived ──
  const totalMods = ALL_COMM_MODS.length;
  const completedCount = Object.values(completed).filter(Boolean).length;
  const progress = totalMods > 0 ? Math.round((completedCount / totalMods) * 100) : 0;
  const toggleDone = id => setCompleted(p => ({ ...p, [id]: !p[id] }));
  const saveNote = () => currentModule && setNotes(p => ({ ...p, [currentModule.id]: noteInput }));

  // ── Persistence ──
  useEffect(() => {
    try {
      const s = localStorage.getItem("flexx_com_v5");
      if (s) {
        const d = JSON.parse(s);
        if (d.completed) setCompleted(d.completed);
        if (d.notes) setNotes(d.notes);
        if (d.quizPassed) setQuizPassed(d.quizPassed);
        if (d.studentName) setStudentName(d.studentName);
        if (d.onboardingDone) { setOnboardingDone(true); setOnboardingResult(d.onboardingResult); }
        if (d.videoUrls) setVideoUrls(d.videoUrls);
        if (d.lbName) setLbName(d.lbName);
        if (d.affiliateCode) setAffiliateCode(d.affiliateCode);
        if (d.threadAuthor) setThreadAuthor(d.threadAuthor);
      }
      const lb = localStorage.getItem("flexx_com_v5_lb");
      if (lb) setLeaderboardData(JSON.parse(lb));
      const th = localStorage.getItem("flexx_com_v5_threads");
      if (th) setThreads(JSON.parse(th));
    } catch(e) {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem("flexx_com_v5", JSON.stringify({ completed, notes, quizPassed, studentName, onboardingDone, onboardingResult, videoUrls, lbName, affiliateCode, threadAuthor })); }
    catch(e) {}
  }, [completed, notes, quizPassed, studentName, onboardingDone, onboardingResult, videoUrls, lbName, affiliateCode, threadAuthor]);

  useEffect(() => {
    try {
      const today = new Date().toDateString();
      const saved = JSON.parse(localStorage.getItem("flexx_com_v5_streak") || "{}");
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      let streak = saved.streak || 0, totalDays = saved.totalDays || 0;
      if (saved.lastVisit !== today) {
        totalDays += 1;
        if (saved.lastVisit === yesterday) streak += 1; else streak = 1;
        const u = { streak, lastVisit: today, totalDays };
        localStorage.setItem("flexx_com_v5_streak", JSON.stringify(u));
        setStreakData(u);
      } else { setStreakData({ streak, lastVisit: today, totalDays: saved.totalDays || 1 }); }
    } catch(e) { setStreakData({ streak: 1, lastVisit: new Date().toDateString(), totalDays: 1 }); }
  }, []);

  // ── Onboarding ──
  const handleOnboardingAnswer = (qId, val) => {
    const next = { ...onboardingAnswers, [qId]: val };
    setOnboardingAnswers(next);
    if (onboardingStep < ONBOARDING_QUIZ.length - 1) setOnboardingStep(s => s + 1);
    else { setOnboardingResult({ track: "commodities" }); setOnboardingDone(true); }
  };

  // ── Quiz ──
  const startQuiz = (moduleId, moduleTitle) => {
    if (!MODULE_QUIZZES[moduleId]) return;
    setActiveQuiz({ moduleId, moduleTitle }); setQuizStep(0); setQuizAnswers({}); setQuizResult(null);
  };
  const handleQuizAnswer = (qIdx, ansIdx) => {
    const next = { ...quizAnswers, [qIdx]: ansIdx };
    setQuizAnswers(next);
    const quiz = MODULE_QUIZZES[activeQuiz.moduleId];
    if (qIdx < quiz.questions.length - 1) setQuizStep(s => s + 1);
    else {
      const score = quiz.questions.reduce((a, q, i) => a + (next[i] === q.correct ? 1 : 0), 0);
      const passed = score >= Math.ceil(quiz.questions.length * 0.75);
      setQuizResult({ passed, score, total: quiz.questions.length });
      if (passed) { setQuizPassed(p => ({ ...p, [activeQuiz.moduleId]: true })); toggleDone(activeQuiz.moduleId); }
    }
  };

  // ── Cert ──
  const generateCert = async (name, moduleTitle, trackLabel) => {
    const date = new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
    const studentDisplayName = name || "Student";
    setShowCert({ name: studentDisplayName, moduleTitle, date, track: trackLabel, affiliate: affiliateCode });
    try {
      const token = localStorage.getItem("flexx_access_token");
      if (!token) return;
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const axios = (await import("axios")).default;
      const { data } = await axios.post(`${API}/flexx/certificate/issue-course`, {
        student_name: studentDisplayName,
        course_name: moduleTitle,
        tier: "intermediate",
        track: trackLabel || "",
        course_subtitle: `${trackLabel || "FLEXX ACADEMY"} — a specialized component of the FLEXX ACADEMY Elite Trading Series`,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (data && data.cert_id) {
        setShowCert((prev) => prev ? { ...prev, verifyCode: data.cert_id, signature: data.signature } : prev);
      }
    } catch (e) {
       
      console.warn("Course cert issuance fell back to local code:", e?.response?.status || e?.message);
    }
  };

  // ── Leaderboard ──
  const submitToLeaderboard = () => {
    if (!lbName.trim()) return;
    const entry = { name:lbName.trim(), completed:completedCount, total:totalMods, pct:progress, streak:streakData.streak, quizzes:Object.values(quizPassed).filter(Boolean).length, track:"COMMODITIES", date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}) };
    try {
      const existing = JSON.parse(localStorage.getItem("flexx_com_v5_lb")||"[]");
      const updated = [entry,...existing.filter(e=>e.name!==entry.name)].sort((a,b)=>b.pct-a.pct||b.streak-a.streak).slice(0,20);
      localStorage.setItem("flexx_com_v5_lb", JSON.stringify(updated));
      setLeaderboardData(updated); setLbSubmitted(true);
    } catch(e) {}
  };

  // ── Admin ──
  const ADMIN_PW = "flexx2026";
  const handleAdminLogin = () => {
    if (adminPwInput === ADMIN_PW) { setAdminMode(true); setShowAdminLogin(false); setAdminPwInput(""); setAdminPwError(false); setView("admin"); }
    else { setAdminPwError(true); }
  };
  const saveVideoUrl = (modId, url) => { setVideoUrls(p=>({...p,[modId]:url.trim()})); setAdminVideoEditing(null); setAdminVideoInput(""); };
  const getYTEmbed = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    const loom = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loom) return `https://www.loom.com/embed/${loom[1]}`;
    return url;
  };

  // ── Search ──
  const runSearch = (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); return; }
    const lower = q.toLowerCase();
    setSearchResults(ALL_COMM_MODS.filter(m => [m.title,m.summary,m.tag,...(m.keyPoints||[])].join(" ").toLowerCase().includes(lower)).slice(0,15));
  };
  useEffect(() => {
    const handler = (e) => { if ((e.metaKey||e.ctrlKey)&&e.key==="k") { e.preventDefault(); setSearchOpen(true); } if (e.key==="Escape") setSearchOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(() => { if (searchOpen&&searchInputRef.current) setTimeout(()=>searchInputRef.current?.focus(),50); }, [searchOpen]);

  // ── Sync ──
  const buildSyncCode = () => {
    try { setSyncCode(btoa(JSON.stringify({ completed,notes,quizPassed,studentName,onboardingDone,onboardingResult,videoUrls,lbName,affiliateCode,streakData,exported:new Date().toISOString(),version:"v5" }))); }
    catch(e) { setSyncCode("ERROR"); }
  };
  const importSyncCode = () => {
    setSyncImportError(""); setSyncImportSuccess(false);
    try {
      const d = JSON.parse(atob(syncImportInput.trim()));
      if (d.version!=="v5") throw new Error("ver");
      if (d.completed) setCompleted(d.completed);
      if (d.notes) setNotes(d.notes);
      if (d.quizPassed) setQuizPassed(d.quizPassed);
      if (d.studentName) setStudentName(d.studentName);
      if (d.onboardingDone) { setOnboardingDone(true); setOnboardingResult(d.onboardingResult); }
      if (d.videoUrls) setVideoUrls(d.videoUrls);
      if (d.lbName) setLbName(d.lbName);
      if (d.affiliateCode) setAffiliateCode(d.affiliateCode);
      setSyncImportSuccess(true); setSyncImportInput("");
    } catch(e) { setSyncImportError("Invalid sync code."); }
  };

  // ── Threads ──
  const saveThreads = (u) => { setThreads(u); try { localStorage.setItem("flexx_com_v5_threads", JSON.stringify(u)); } catch(e) {} };
  const postThread = (moduleId) => {
    if (!threadInput.trim()) return;
    const entry = { id:Date.now(), author:threadAuthor.trim()||"Anonymous", text:threadInput.trim(), ts:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}), upvotes:0 };
    saveThreads({ ...threads, [moduleId]:[entry,...(threads[moduleId]||[])] });
    setThreadInput("");
  };
  const upvoteThread = (moduleId, entryId) => {
    const key = `upvoted_${moduleId}_${entryId}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key,"1");
    saveThreads({ ...threads, [moduleId]:(threads[moduleId]||[]).map(e=>e.id===entryId?{...e,upvotes:e.upvotes+1}:e).sort((a,b)=>b.upvotes-a.upvotes) });
  };

  // ── SageMaster Product Modal ──
  const SageMasterModal = () => {
    if (!sageMasterOpen) return null;
    const prod = sageMasterProduct ? SAGEMASTER_PRODUCTS.find(p => p.id === sageMasterProduct) : null;
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:280, overflow:"auto", padding:"20px 16px" }}>
        <div style={{ maxWidth:700, margin:"0 auto" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:8, color:B.gold, letterSpacing:4, fontFamily:"monospace" }}>⚡ FLEXX × SAGEMASTER PRODUCT SUITE</div>
            <button onClick={() => { setSageMasterOpen(false); setSageMasterProduct(null); }} style={{ padding:"6px 12px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>✕ CLOSE</button>
          </div>

          {!prod ? (
            // Product grid
            <>
              <div style={{ padding:"18px 20px", marginBottom:16, background:B.blackCard, border:`1px solid ${B.gold+"40"}`, borderTop:`3px solid ${B.gold}` }}>
                <div style={{ fontSize:16, fontWeight:700, color:B.white, marginBottom:6 }}>SageMaster AI Trading Suite</div>
                <div style={{ fontSize:12, color:B.whiteGray, lineHeight:1.7, marginBottom:12 }}>
                  SageMaster is the AI trading platform built for the FLEXX system. Each tool in the suite maps directly to a step in your FLEXX trading process — from scanning to execution.
                </div>
                <a href="https://sagemaster.com" target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", padding:"8px 18px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:9, letterSpacing:3, fontFamily:"monospace", fontWeight:700 }}>
                  VISIT SAGEMASTER.COM →
                </a>
              </div>
              <div style={{ display:"grid", gap:8, gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))" }}>
                {SAGEMASTER_PRODUCTS.map(p => (
                  <div key={p.id} className="hov-row" onClick={() => setSageMasterProduct(p.id)} style={{ padding:"14px 16px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, borderLeft:`3px solid ${p.color}`, cursor:"pointer" }}>
                    <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                      <div style={{ fontSize:22, flexShrink:0 }}>{p.icon}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", gap:8, alignItems:"baseline", flexWrap:"wrap", marginBottom:4 }}>
                          <span style={{ fontSize:14, fontWeight:700, color:B.white }}>{p.name}</span>
                          <span style={{ fontSize:8, padding:"2px 7px", color:p.color, border:`1px solid ${p.color+"40"}`, fontFamily:"monospace", letterSpacing:1 }}>{p.tag}</span>
                        </div>
                        <div style={{ fontSize:11, color:B.whiteGray, lineHeight:1.5 }}>{p.tagline}</div>
                      </div>
                      <span style={{ color:B.gold, fontSize:14, flexShrink:0 }}>›</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            // Single product detail
            <div style={{ background:B.blackCard, border:`1px solid ${prod.color+"40"}`, borderTop:`3px solid ${prod.color}` }}>
              <div style={{ padding:"22px 24px" }}>
                <button onClick={() => setSageMasterProduct(null)} style={{ background:"transparent", border:"none", color:B.whiteMuted, cursor:"pointer", fontFamily:"monospace", fontSize:11, marginBottom:16 }}>← ALL TOOLS</button>
                <div style={{ fontSize:32, marginBottom:8 }}>{prod.icon}</div>
                <div style={{ fontSize:8, color:prod.color, letterSpacing:3, marginBottom:6, fontFamily:"monospace" }}>{prod.tag}</div>
                <div style={{ fontSize:22, fontWeight:700, color:B.white, marginBottom:8 }}>{prod.name}</div>
                <div style={{ fontSize:14, color:B.whiteGray, fontStyle:"italic", lineHeight:1.7, marginBottom:16 }}>{prod.tagline}</div>
                <div style={{ padding:"14px 16px", background:`${prod.color}10`, border:`1px solid ${prod.color+"30"}`, marginBottom:16 }}>
                  <div style={{ fontSize:8, color:prod.color, letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>◆ WHAT IT DOES</div>
                  <div style={{ fontSize:13, color:B.whiteGray, lineHeight:1.75 }}>{prod.desc}</div>
                </div>
                <div style={{ padding:"14px 16px", background:B.goldFaint, border:`1px solid ${B.gold+"30"}`, borderLeft:`3px solid ${B.gold}`, marginBottom:20 }}>
                  <div style={{ fontSize:8, color:B.gold, letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>⚡ FLEXX USE CASE — HOW TO USE THIS IN YOUR TRADING</div>
                  <div style={{ fontSize:13, color:B.whiteGray, lineHeight:1.75 }}>{prod.useCase}</div>
                </div>
                <a href={prod.url} target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", padding:"10px 22px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:10, letterSpacing:3, fontFamily:"monospace", fontWeight:700 }}>
                  OPEN {prod.name.toUpperCase()} →
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Leaderboard Modal ──
  const LeaderboardModal = () => {
    if (!showLeaderboard) return null;
    const myRank = leaderboardData.findIndex(e => e.name === lbName) + 1;
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:280, overflow:"auto", padding:"20px 16px" }}>
        <div style={{ maxWidth:600, margin:"0 auto" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:8, color:B.gold, letterSpacing:4, fontFamily:"monospace" }}>🏆 FLEXX ACADEMY LEADERBOARD</div>
            <button onClick={() => setShowLeaderboard(false)} style={{ padding:"6px 12px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>✕ CLOSE</button>
          </div>

          {/* Submit your score */}
          {!lbSubmitted ? (
            <div style={{ padding:"16px 18px", marginBottom:16, background:B.blackCard, border:`1px solid ${B.gold+"40"}`, borderTop:`3px solid ${B.gold}` }}>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:3, marginBottom:8, fontFamily:"monospace" }}>ADD YOUR SCORE TO THE BOARD</div>
              <div style={{ fontSize:11, color:B.whiteGray, marginBottom:12 }}>Enter your name to add your current progress ({progress}% · {streakData.streak} day streak · {Object.values(quizPassed).filter(Boolean).length} quizzes) to the leaderboard.</div>
              <div style={{ display:"flex", gap:8 }}>
                <input value={lbName} onChange={e => setLbName(e.target.value)} placeholder="Your name or nickname" style={{ flex:1, padding:"8px 12px", background:B.blackPanel, border:`1px solid ${B.gold+"40"}`, color:B.white, fontSize:12, fontFamily:"Georgia,serif", outline:"none" }} onKeyDown={e => e.key === "Enter" && submitToLeaderboard()} />
                <button className="hov" onClick={submitToLeaderboard} style={{ padding:"8px 16px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:9, letterSpacing:2, fontFamily:"monospace", fontWeight:700, border:"none", cursor:"pointer" }}>
                  POST →
                </button>
              </div>
            </div>
          ) : (
            <div style={{ padding:"12px 16px", marginBottom:16, background:B.goldFaint, border:`1px solid ${B.gold+"30"}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:11, color:B.gold, fontFamily:"monospace" }}>
                {myRank > 0 ? `You're ranked #${myRank} — keep going!` : "Your score is posted!"}
              </div>
              <button className="hov" onClick={() => { setLbSubmitted(false); submitToLeaderboard(); }} style={{ padding:"5px 12px", background:"transparent", color:B.gold, border:`1px solid ${B.gold+"50"}`, fontSize:9, letterSpacing:1, fontFamily:"monospace", cursor:"pointer" }}>
                UPDATE SCORE
              </button>
            </div>
          )}

          {/* Board */}
          {leaderboardData.length === 0 ? (
            <div style={{ padding:"30px", textAlign:"center", color:B.whiteMuted, fontFamily:"monospace", fontSize:11 }}>
              No entries yet. Be the first to post your score!
            </div>
          ) : (
            <div style={{ display:"grid", gap:5 }}>
              {leaderboardData.map((entry, i) => {
                const isMe = entry.name === lbName;
                const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i+1}.`;
                return (
                  <div key={i} style={{ padding:"12px 16px", background:isMe ? B.goldFaint : B.blackCard, border:`1px solid ${isMe ? B.gold+"40" : B.blackBorder}`, display:"flex", gap:12, alignItems:"center" }}>
                    <div style={{ width:32, textAlign:"center", fontSize:16, flexShrink:0 }}>{medal}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:isMe ? B.gold : B.white, marginBottom:2 }}>{entry.name}{isMe ? " (you)" : ""}</div>
                      <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>{entry.track} · {entry.date}</div>
                    </div>
                    <div style={{ display:"flex", gap:12, flexShrink:0, textAlign:"right" }}>
                      <div>
                        <div style={{ fontSize:16, fontWeight:700, color:B.gold, fontFamily:"monospace" }}>{entry.pct}%</div>
                        <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace" }}>{entry.completed}/{entry.total}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:B.goldLight, fontFamily:"monospace" }}>🔥{entry.streak}</div>
                        <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace" }}>STREAK</div>
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:B.green, fontFamily:"monospace" }}>🏆{entry.quizzes}</div>
                        <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace" }}>QUIZZES</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop:12, fontSize:9, color:B.whiteMuted, fontFamily:"monospace", textAlign:"center" }}>
            Leaderboard stored locally in your browser. Share your rank to recruit new students to BE/FLEXX.
          </div>
        </div>
      </div>
    );
  };

  // ── Admin Login Modal ──

  // ── Admin Login Modal (inline to prevent focus loss) ──

  // ── Admin View ──
  const AdminView = () => {
    if (view !== "admin" || !adminMode) return null;
    return (
      <div style={{ maxWidth:900, margin:"0 auto", padding:"22px 16px" }}>
        <div style={{ padding:"16px 18px", marginBottom:20, background:B.blackCard, border:`1px solid ${B.gold+"50"}`, borderTop:`3px solid ${B.gold}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
            <div>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:4, marginBottom:4, fontFamily:"monospace" }}>🎬 ADMIN MEDIA DASHBOARD</div>
              <div style={{ fontSize:16, fontWeight:700, color:B.white }}>Lesson Video Management</div>
              <div style={{ fontSize:11, color:B.whiteGray, marginTop:4 }}>Paste YouTube or Loom URLs per lesson. Students will see an embedded player at the top of each lesson.</div>
            </div>
            <button className="hov" onClick={() => { setAdminMode(false); setView("map"); }} style={{ padding:"7px 14px", background:"transparent", color:B.red, border:`1px solid ${B.red+"50"}`, fontSize:9, letterSpacing:2, fontFamily:"monospace", cursor:"pointer" }}>
              EXIT ADMIN
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
          {[
            { label:"TOTAL LESSONS", val:ALL_COMM_MODS.length, col:B.gold },
            { label:"VIDEOS ADDED", val:Object.values(videoUrls).filter(Boolean).length, col:B.green },
            { label:"LESSONS WITHOUT VIDEO", val:ALL_COMM_MODS.length - Object.values(videoUrls).filter(Boolean).length, col:B.red },
          ].map((s, i) => (
            <div key={i} style={{ padding:"10px 14px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, borderTop:`2px solid ${s.col+"40"}`, flex:1, minWidth:120 }}>
              <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace", letterSpacing:2, marginBottom:4 }}>{s.label}</div>
              <div style={{ fontSize:22, fontWeight:700, color:s.col, fontFamily:"monospace" }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Module list by track */}
        {track.phases.map(ph => (
          <div key={ph.id} style={{ marginBottom:24 }}>
            <div style={{ fontSize:8, color:accent, letterSpacing:3, marginBottom:10, fontFamily:"monospace", padding:"6px 0", borderBottom:`1px solid ${B.blackBorder}` }}>
              {track.label} · {ph.label}
            </div>
                <div style={{ display:"grid", gap:5 }}>
                  {ph.modules.map(mod => {
                    const hasVideo = !!(videoUrls[mod.id]);
                    const isEditing = adminVideoEditing === mod.id;
                    return (
                      <div key={mod.id} style={{ padding:"12px 14px", background:B.blackCard, border:`1px solid ${hasVideo ? B.green+"30" : B.blackBorder}` }}>
                        <div style={{ display:"flex", gap:10, alignItems:"flex-start", flexWrap:"wrap" }}>
                          <div style={{ flex:1, minWidth:200 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:B.white, marginBottom:2 }}>{mod.title}</div>
                            <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>{mod.id} · {mod.duration}</div>
                          </div>
                          <div style={{ flex:2, minWidth:250 }}>
                            {isEditing ? (
                              <div style={{ display:"flex", gap:6 }}>
                                <input value={adminVideoInput} onChange={e => setAdminVideoInput(e.target.value)} onKeyDown={e => e.key === "Enter" && saveVideoUrl(mod.id, adminVideoInput)} placeholder="https://youtube.com/watch?v=... or loom.com/share/..." style={{ flex:1, padding:"6px 10px", background:B.blackPanel, border:`1px solid ${B.gold+"50"}`, color:B.white, fontSize:11, fontFamily:"monospace", outline:"none" }} autoFocus />
                                <button onClick={() => saveVideoUrl(mod.id, adminVideoInput)} style={{ padding:"6px 12px", background:B.gold, color:B.black, border:"none", cursor:"pointer", fontFamily:"monospace", fontSize:9, fontWeight:700 }}>SAVE</button>
                                <button onClick={() => { setAdminVideoEditing(null); setAdminVideoInput(""); }} style={{ padding:"6px 10px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer", fontFamily:"monospace", fontSize:9 }}>✕</button>
                              </div>
                            ) : (
                              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                                {hasVideo ? (
                                  <div style={{ fontSize:10, color:B.green, fontFamily:"monospace", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                    ✓ {videoUrls[mod.id].slice(0, 55)}{videoUrls[mod.id].length > 55 ? "..." : ""}
                                  </div>
                                ) : (
                                  <div style={{ fontSize:10, color:B.whiteMuted, fontFamily:"monospace", flex:1 }}>No video URL set</div>
                                )}
                                <button className="hov" onClick={() => { setAdminVideoEditing(mod.id); setAdminVideoInput(videoUrls[mod.id] || ""); }} style={{ padding:"4px 10px", background:"transparent", color:accent, border:`1px solid ${accent+"50"}`, fontSize:9, letterSpacing:1, fontFamily:"monospace", cursor:"pointer", flexShrink:0 }}>
                                  {hasVideo ? "EDIT" : "+ ADD VIDEO"}
                                </button>
                                {hasVideo && (
                                  <button className="hov" onClick={() => saveVideoUrl(mod.id, "")} style={{ padding:"4px 8px", background:"transparent", color:B.red, border:`1px solid ${B.red+"40"}`, fontSize:9, fontFamily:"monospace", cursor:"pointer", flexShrink:0 }}>
                                    REMOVE
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
          </div>
        ))}
      </div>
    );
  };

  // ── FEATURE 7: Global Search Modal ──
  const SearchModal = () => {
    if (!searchOpen) return null;
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:400, display:"flex", flexDirection:"column", padding:"60px 16px 20px" }} onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}>
        <div style={{ maxWidth:680, width:"100%", margin:"0 auto" }}>
          {/* Search input */}
          <div style={{ display:"flex", gap:10, marginBottom:12, background:B.blackCard, border:`1px solid ${B.gold+"60"}`, borderTop:`3px solid ${B.gold}`, padding:"14px 16px" }}>
            <span style={{ color:B.gold, fontSize:18, flexShrink:0 }}>🔍</span>
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={e => runSearch(e.target.value)}
              placeholder="Search lessons, concepts, modules, strategies..."
              style={{ flex:1, background:"transparent", border:"none", color:B.white, fontSize:15, fontFamily:"Georgia,serif", outline:"none" }}
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(""); setSearchResults([]); }} style={{ background:"transparent", border:"none", color:B.whiteMuted, cursor:"pointer", fontSize:14 }}>✕</button>
            )}
            <button onClick={() => setSearchOpen(false)} style={{ background:"transparent", border:`1px solid ${B.blackBorder}`, color:B.whiteMuted, cursor:"pointer", fontSize:9, fontFamily:"monospace", padding:"4px 10px" }}>ESC</button>
          </div>

          {/* Hints */}
          {!searchQuery && (
            <div style={{ padding:"16px 18px", background:B.blackCard, border:`1px solid ${B.blackBorder}` }}>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:3, marginBottom:12, fontFamily:"monospace" }}>SEARCH ACROSS ALL TRACKS, LEVELS & LESSONS</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {["candlestick", "support resistance", "risk management", "smart money", "SageMaster", "EMA", "fibonacci", "journal"].map(hint => (
                  <button key={hint} className="hov" onClick={() => runSearch(hint)} style={{ padding:"5px 12px", background:"transparent", border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:11, fontFamily:"monospace", cursor:"pointer" }}>
                    {hint}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          {searchQuery && (
            <div style={{ maxHeight:"55vh", overflowY:"auto" }}>
              {searchResults.length === 0 ? (
                <div style={{ padding:"24px 18px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, textAlign:"center", color:B.whiteMuted, fontSize:12, fontFamily:"monospace" }}>
                  No results for "{searchQuery}" — try different keywords
                </div>
              ) : (
                <>
                  <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace", marginBottom:6, padding:"0 4px" }}>
                    {searchResults.length} RESULT{searchResults.length !== 1 ? "S" : ""} FOR "{searchQuery.toUpperCase()}"
                  </div>
                  <div style={{ display:"grid", gap:5 }}>
                    {searchResults.map((mod, i) => {
                      const hilite = (str) => {
                        if (!str) return str;
                        const idx = str.toLowerCase().indexOf(searchQuery.toLowerCase());
                        if (idx === -1) return str;
                        return <>{str.slice(0, idx)}<mark style={{ background:B.gold+"40", color:B.gold, padding:"0 2px" }}>{str.slice(idx, idx + searchQuery.length)}</mark>{str.slice(idx + searchQuery.length)}</>;
                      };
                      return (
                        <div key={i} className="hov-row" onClick={() => {
                          setTrack(mod.trackId);
                          setLevel(mod.levelId);
                          setPhaseIdx(mod.phaseIdx);
                          openLesson(mod, mod.phaseIdx);
                          setSearchOpen(false);
                          setSearchQuery("");
                          setSearchResults([]);
                        }} style={{ padding:"12px 16px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, cursor:"pointer" }}>
                          <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:3, alignItems:"baseline" }}>
                                <span style={{ fontSize:14, fontWeight:700, color:B.white }}>{hilite(mod.title)}</span>
                                <span style={{ fontSize:8, padding:"2px 7px", color:mod.trackAccent, border:`1px solid ${mod.trackAccent+"40"}`, fontFamily:"monospace" }}>{mod.tag}</span>
                                {completed[mod.id] && <span style={{ fontSize:8, padding:"2px 7px", color:B.green, border:`1px solid ${B.green+"40"}`, fontFamily:"monospace" }}>✓ DONE</span>}
                              </div>
                              <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace", marginBottom:4 }}>
                                {mod.trackLabel} · {mod.levelLabel} · {mod.phaseLabel}
                              </div>
                              <div style={{ fontSize:11, color:B.whiteGray, lineHeight:1.5 }}>{hilite(mod.summary?.slice(0,100))}...</div>
                            </div>
                            <span style={{ color:mod.trackAccent, fontSize:16, flexShrink:0 }}>›</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── FEATURE 8: Sync Modal ──
  const SyncModal = () => {
    if (!syncOpen) return null;
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:380, overflow:"auto", padding:"20px 16px" }}>
        <div style={{ maxWidth:580, margin:"0 auto" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:8, color:B.gold, letterSpacing:4, fontFamily:"monospace" }}>📱 CROSS-DEVICE SYNC</div>
            <button onClick={() => { setSyncOpen(false); setSyncCode(""); setSyncImportInput(""); setSyncImportError(""); setSyncImportSuccess(false); }} style={{ padding:"6px 12px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>✕ CLOSE</button>
          </div>

          {/* EXPORT */}
          <div style={{ padding:"18px 20px", marginBottom:12, background:B.blackCard, border:`1px solid ${B.gold+"40"}`, borderTop:`3px solid ${B.gold}` }}>
            <div style={{ fontSize:8, color:B.gold, letterSpacing:3, marginBottom:8, fontFamily:"monospace" }}>📤 EXPORT — COPY YOUR PROGRESS CODE</div>
            <div style={{ fontSize:12, color:B.whiteGray, marginBottom:14, lineHeight:1.65 }}>
              Generate a sync code that captures your entire progress — completed lessons, notes, quiz passes, streak, settings. Copy it and paste it on any other device to continue right where you left off.
            </div>
            <button className="hov" onClick={buildSyncCode} style={{ padding:"10px 20px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:10, letterSpacing:3, fontFamily:"monospace", fontWeight:700, border:"none", cursor:"pointer", marginBottom: syncCode ? 12 : 0 }}>
              GENERATE SYNC CODE →
            </button>
            {syncCode && (
              <>
                <div style={{ padding:"10px 12px", background:B.blackPanel, border:`1px solid ${B.blackBorder}`, fontFamily:"monospace", fontSize:10, color:B.whiteGray, wordBreak:"break-all", lineHeight:1.6, marginBottom:8 }}>
                  {syncCode.slice(0, 120)}...
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button className="hov" onClick={() => { navigator.clipboard?.writeText(syncCode).catch(() => {}); }} style={{ flex:1, padding:"8px", background:"transparent", color:B.green, border:`1px solid ${B.green+"50"}`, fontSize:9, letterSpacing:2, fontFamily:"monospace", cursor:"pointer" }}>
                    📋 COPY FULL CODE
                  </button>
                  <button className="hov" onClick={() => {
                    const blob = new Blob([syncCode], { type:"text/plain" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = "flexx_sync.txt"; a.click();
                    URL.revokeObjectURL(url);
                  }} style={{ flex:1, padding:"8px", background:"transparent", color:B.gold, border:`1px solid ${B.gold+"50"}`, fontSize:9, letterSpacing:2, fontFamily:"monospace", cursor:"pointer" }}>
                    ⬇ DOWNLOAD AS FILE
                  </button>
                </div>
                <div style={{ marginTop:8, fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>
                  Exported: {progress}% complete · {completedCount} lessons · 🔥{streakData.streak} streak
                </div>
              </>
            )}
          </div>

          {/* IMPORT */}
          <div style={{ padding:"18px 20px", background:B.blackCard, border:`1px solid ${B.blackBorder}` }}>
            <div style={{ fontSize:8, color:accent, letterSpacing:3, marginBottom:8, fontFamily:"monospace" }}>📥 IMPORT — PASTE YOUR SYNC CODE</div>
            <div style={{ fontSize:12, color:B.whiteGray, marginBottom:12 }}>
              Paste a sync code from another device to restore your progress here. This will overwrite your current progress on this device.
            </div>
            <textarea
              value={syncImportInput}
              onChange={e => { setSyncImportInput(e.target.value); setSyncImportError(""); setSyncImportSuccess(false); }}
              placeholder="Paste your sync code here..."
              style={{ width:"100%", minHeight:80, padding:10, background:B.blackPanel, border:`1px solid ${syncImportError ? B.red+"60" : B.blackBorder}`, color:B.whiteGray, fontSize:11, fontFamily:"monospace", outline:"none", resize:"vertical", lineHeight:1.5, marginBottom:8 }}
            />
            {syncImportError && <div style={{ fontSize:10, color:B.red, fontFamily:"monospace", marginBottom:8 }}>{syncImportError}</div>}
            {syncImportSuccess && <div style={{ fontSize:10, color:B.green, fontFamily:"monospace", marginBottom:8 }}>✓ Progress imported successfully! Your data has been restored.</div>}
            <button className="hov" onClick={importSyncCode} disabled={!syncImportInput.trim()} style={{ padding:"10px 20px", background:syncImportInput.trim() ? `linear-gradient(135deg,${accent},${B.goldDark})` : B.blackBorder, color:syncImportInput.trim() ? B.black : B.whiteMuted, fontSize:10, letterSpacing:3, fontFamily:"monospace", fontWeight:700, border:"none", cursor:syncImportInput.trim() ? "pointer" : "default" }}>
              IMPORT & RESTORE →
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── FEATURE 9: Community Thread Component (used inside lesson view) ──
  const LessonThread = ({ moduleId }) => {
    const entries = (threads[moduleId] || []);
    return (
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:8, color:B.whiteGray, letterSpacing:3, marginBottom:10, fontFamily:"monospace" }}>💬 STUDENT DISCUSSION ({entries.length})</div>

        {/* Post form */}
        <div style={{ padding:"14px 16px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, marginBottom:10 }}>
          <div style={{ display:"flex", gap:8, marginBottom:8 }}>
            <input
              value={threadAuthor}
              onChange={e => setThreadAuthor(e.target.value)}
              placeholder="Your name (optional)"
              style={{ flex:"0 0 140px", padding:"7px 10px", background:B.blackPanel, border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:11, fontFamily:"monospace", outline:"none" }}
            />
            <input
              value={threadInput}
              onChange={e => setThreadInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && postThread(moduleId)}
              placeholder="Share a question, insight, or tip from this lesson..."
              style={{ flex:1, padding:"7px 10px", background:B.blackPanel, border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:12, fontFamily:"Georgia,serif", outline:"none" }}
            />
            <button className="hov" onClick={() => postThread(moduleId)} style={{ padding:"7px 14px", background:`linear-gradient(135deg,${accent},${B.goldDark})`, color:B.black, fontSize:9, letterSpacing:2, fontFamily:"monospace", fontWeight:700, border:"none", cursor:"pointer", flexShrink:0 }}>
              POST
            </button>
          </div>
          <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>Press Enter to post · Upvote the most helpful comments</div>
        </div>

        {/* Thread entries */}
        {entries.length === 0 ? (
          <div style={{ padding:"16px", textAlign:"center", color:B.whiteMuted, fontSize:11, fontFamily:"monospace", border:`1px solid ${B.blackBorder}` }}>
            No comments yet — be the first to share an insight from this lesson.
          </div>
        ) : (
          <div style={{ display:"grid", gap:6 }}>
            {entries.map((entry) => {
              const alreadyVoted = (() => { try { return !!localStorage.getItem(`upvoted_${moduleId}_${entry.id}`); } catch(e) { return false; } })();
              return (
                <div key={entry.id} style={{ padding:"12px 14px", background:B.blackCard, border:`1px solid ${B.blackBorder}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, flexWrap:"wrap", gap:4 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:accent }}>{entry.author}</span>
                    <span style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>{entry.ts}</span>
                  </div>
                  <div style={{ fontSize:13, color:B.whiteGray, lineHeight:1.65, marginBottom:8 }}>{entry.text}</div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <button className="hov" onClick={() => upvoteThread(moduleId, entry.id)} style={{ padding:"3px 10px", background:alreadyVoted ? `${accent}20` : "transparent", color:alreadyVoted ? accent : B.whiteMuted, border:`1px solid ${alreadyVoted ? accent+"50" : B.blackBorder}`, fontSize:9, fontFamily:"monospace", cursor:"pointer" }}>
                      ▲ {entry.upvotes} {entry.upvotes === 1 ? "upvote" : "upvotes"}
                    </button>
                    {(entry.author === (threadAuthor || "Anonymous")) && (
                      <button className="hov" onClick={() => deleteThread(moduleId, entry.id)} style={{ padding:"3px 8px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, fontSize:9, fontFamily:"monospace", cursor:"pointer" }}>
                        delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Onboarding Quiz Modal ──
  const OnboardingQuiz = () => {
    if (onboardingDone) return null;
    const q = ONBOARDING_QUIZ[onboardingStep];
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.97)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
        <div style={{ maxWidth:560, width:"100%", background:B.blackCard, border:`1px solid ${B.gold+"50"}`, borderTop:`3px solid ${B.gold}` }}>
          {/* Header */}
          <div style={{ padding:"24px 26px 0" }}>
            <img src={FLEXX_LOGO} alt="FLEXX" style={{ height:32, marginBottom:18, opacity:0.9 }} />
            <div style={{ fontSize:8, color:B.gold, letterSpacing:4, marginBottom:10, fontFamily:"monospace" }}>STUDENT PLACEMENT · QUESTION {onboardingStep+1} OF {ONBOARDING_QUIZ.length}</div>
            {/* Progress bar */}
            <div style={{ height:2, background:B.blackBorder, marginBottom:20 }}>
              <div style={{ width:`${((onboardingStep)/ONBOARDING_QUIZ.length)*100}%`, height:"100%", background:B.gold, transition:"width 0.4s" }} />
            </div>
            <div style={{ fontSize:18, fontWeight:700, color:B.white, lineHeight:1.5, marginBottom:22 }}>{q.question}</div>
          </div>
          {/* Options */}
          <div style={{ padding:"0 26px 28px", display:"grid", gap:8 }}>
            {q.options.map((opt, i) => (
              <button key={i} className="hov" onClick={() => handleOnboardingAnswer(q.id, opt.value)} style={{ padding:"13px 16px", background:"transparent", border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:13, textAlign:"left", cursor:"pointer", transition:"all 0.2s", fontFamily:"Georgia,serif", lineHeight:1.5 }}>
                <span style={{ color:B.gold, fontFamily:"monospace", fontSize:10, marginRight:10 }}>{String.fromCharCode(65+i)}.</span>
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ padding:"0 26px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>FLEXX ACADEMY · TRACK PLACEMENT</div>
            {onboardingStep > 0 && (
              <button onClick={() => setOnboardingStep(s => s-1)} style={{ background:"transparent", border:"none", color:B.whiteMuted, fontSize:11, cursor:"pointer", fontFamily:"monospace" }}>← BACK</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Module Quiz Modal ──
  const QuizModal = () => {
    if (!activeQuiz) return null;
    const quiz = MODULE_QUIZZES[activeQuiz.moduleId];
    if (!quiz) return null;

    // Results screen
    if (quizResult) {
      return (
        <FlexxQuizResultPanel
          passed={quizResult.passed}
          score={quizResult.score}
          total={quizResult.total}
          moduleTitle={activeQuiz.moduleTitle}
          tierLabel={track.label}
          studentName={studentName}
          setStudentName={setStudentName}
          affiliateCode={affiliateCode}
          setAffiliateCode={setAffiliateCode}
          affiliatePlaceholder="BE Referral Code (optional — e.g. FLEXX123)"
          onGenerateCert={() => { generateCert(studentName, activeQuiz.moduleTitle, track.label); setActiveQuiz(null); setQuizResult(null); }}
          onTryAgain={() => { setQuizStep(0); setQuizAnswers({}); setQuizResult(null); }}
          onClose={() => { setActiveQuiz(null); setQuizResult(null); }}
        />
      );
    }

    // Active question
    const q = quiz.questions[quizStep];
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:250, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
        <div style={{ maxWidth:560, width:"100%", background:B.blackCard, border:`1px solid ${B.gold+"50"}`, borderTop:`3px solid ${B.gold}` }}>
          <div style={{ padding:"24px 26px 0" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:16 }}>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:3, fontFamily:"monospace" }}>MODULE QUIZ · {quiz.moduleTitle.toUpperCase()}</div>
              <button onClick={() => setActiveQuiz(null)} style={{ background:"transparent", border:"none", color:B.whiteMuted, cursor:"pointer", fontFamily:"monospace", fontSize:11 }}>✕</button>
            </div>
            {/* Progress */}
            <div style={{ height:2, background:B.blackBorder, marginBottom:6 }}>
              <div style={{ width:`${(quizStep/quiz.questions.length)*100}%`, height:"100%", background:B.gold, transition:"width 0.3s" }} />
            </div>
            <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace", marginBottom:18 }}>QUESTION {quizStep+1} OF {quiz.questions.length}</div>
            <div style={{ fontSize:16, fontWeight:700, color:B.white, lineHeight:1.6, marginBottom:22 }}>{q.q}</div>
          </div>
          <div style={{ padding:"0 26px 26px", display:"grid", gap:8 }}>
            {q.options.map((opt, i) => (
              <button key={i} className="hov" onClick={() => handleQuizAnswer(quizStep, i)} style={{ padding:"12px 16px", background:"transparent", border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:13, textAlign:"left", cursor:"pointer", fontFamily:"Georgia,serif", lineHeight:1.4 }}>
                <span style={{ color:B.gold, fontFamily:"monospace", fontSize:10, marginRight:10 }}>{String.fromCharCode(65+i)}.</span>
                {opt}
              </button>
            ))}
          </div>
          <div style={{ padding:"0 26px 18px", fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>Need 75% (3/4) to pass and earn your certificate</div>
        </div>
      </div>
    );
  };

  // ── Certificate Modal ──
  const CertModal = () => {
    if (!showCert) return null;
    const verifyCode = showCert.verifyCode || (Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4));
    const courseSubtitle = `${showCert.track || "FLEXX ACADEMY"} — a specialized component of the FLEXX ACADEMY Elite Trading Series`;
    const onDownloadPdf = () => openCertificatePrintView({
      studentName: showCert.name,
      courseName: showCert.moduleTitle,
      courseSubtitle,
      completionDate: showCert.date,
      verifyCode,
      tier: "intermediate",
    });
    return (
      <FlexxCertificate
        studentName={showCert.name}
        courseName={showCert.moduleTitle}
        courseSubtitle={courseSubtitle}
        completionDate={showCert.date}
        verifyCode={verifyCode}
        tier="intermediate"
        onClose={() => setShowCert(null)}
        onDownload={onDownloadPdf}
      />
    );
  };


  return (
    <div style={{
      minHeight: "100vh", background: B.black, color: B.whiteOff,
      fontFamily: "Georgia, serif", padding: 0,
    }}>
      <OnboardingQuiz />
      {activeQuiz && QuizModal()}
      {showCert && <CertModal />}
      {sageMasterOpen && <SageMasterModal />}
      {showLeaderboard && <LeaderboardModal />}
      {showAdminLogin && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.97)", zIndex:290, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
          <div style={{ maxWidth:400, width:"100%", background:B.blackCard, border:`1px solid ${B.gold+"50"}`, borderTop:`3px solid ${B.gold}` }}>
            <div style={{ padding:"28px 26px" }}>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:4, marginBottom:12, fontFamily:"monospace" }}>🔐 ADMIN ACCESS</div>
              <div style={{ fontSize:16, fontWeight:700, color:B.white, marginBottom:6 }}>Media Dashboard</div>
              <div style={{ fontSize:12, color:B.whiteGray, marginBottom:20 }}>Enter the admin password to manage video content for each lesson.</div>
              <input type="password" value={adminPwInput} onChange={e => { setAdminPwInput(e.target.value); setAdminPwError(false); }} onKeyDown={e => e.key === "Enter" && handleAdminLogin()} placeholder="Admin password" autoFocus style={{ width:"100%", padding:"10px 12px", background:B.blackPanel, border:`1px solid ${adminPwError ? B.red+"60" : B.gold+"40"}`, color:B.white, fontSize:13, fontFamily:"monospace", outline:"none", marginBottom:adminPwError ? 6 : 12 }} />
              {adminPwError && <div style={{ fontSize:10, color:B.red, fontFamily:"monospace", marginBottom:10 }}>Incorrect password</div>}
              <div style={{ display:"flex", gap:8 }}>
                <button className="hov" onClick={handleAdminLogin} style={{ flex:1, padding:"10px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:9, letterSpacing:3, fontFamily:"monospace", fontWeight:700, border:"none", cursor:"pointer" }}>ENTER →</button>
                <button className="hov" onClick={() => { setShowAdminLogin(false); setAdminPwInput(""); setAdminPwError(false); }} style={{ padding:"10px 16px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, fontSize:9, letterSpacing:2, fontFamily:"monospace", cursor:"pointer" }}>CANCEL</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {searchOpen && <SearchModal />}
      {syncOpen && <SyncModal />}
      {view === "admin" && adminMode && <AdminView />}

      {/* ── HEADER ── */}
      <div style={{ borderBottom: `1px solid ${B.blackBorder}`, background: B.blackDeep }}>
        <GoldBar color={accent} />
        <div style={{ padding: "24px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 4,
              background: `linear-gradient(135deg, ${accent}, ${B.goldDark})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 28, color: B.black, fontWeight: "bold",
            }}>{track.icon}</div>
            <div>
              <div style={{ fontSize: 10, color: B.whiteMuted, fontFamily: "monospace", letterSpacing: 3 }}>FLEXX ACADEMY · FILE 04</div>
              <div style={{ fontSize: 22, color: B.white, letterSpacing: 1, marginTop: 4 }}>{track.label}</div>
              <div style={{ fontSize: 11, color: accent, fontFamily: "monospace", letterSpacing: 1, marginTop: 2 }}>{track.tagline}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="hov-btn" onClick={() => setView("map")} style={{
              padding: "8px 14px", fontSize: 10, fontFamily: "monospace", letterSpacing: 2,
              background: view === "map" ? accent : "transparent", color: view === "map" ? B.black : B.whiteGray,
              border: `1px solid ${accent}40`, cursor: "pointer",
            }}>COURSE MAP</button>
            <button className="hov-btn" onClick={() => setView("credit")} style={{
              padding: "8px 14px", fontSize: 10, fontFamily: "monospace", letterSpacing: 2,
              background: view === "credit" ? accent : "transparent", color: view === "credit" ? B.black : B.whiteGray,
              border: `1px solid ${accent}40`, cursor: "pointer",
            }}>CREDITS</button>
            <button className="hov-btn" onClick={() => setSearchOpen(true)} style={{ padding:"8px 14px", fontSize:10, fontFamily:"monospace", letterSpacing:2, background:"transparent", color:B.whiteGray, border:`1px solid ${B.blackBorder}`, cursor:"pointer" }}>🔍</button>
            <button className="hov-btn" onClick={() => { setSageMasterProduct(null); setSageMasterOpen(true); }} style={{ padding:"8px 14px", fontSize:10, fontFamily:"monospace", letterSpacing:2, background:"transparent", color:"#4A9EFF", border:"1px solid #4A9EFF60", cursor:"pointer" }}>⚡ SAGE</button>
            <button className="hov-btn" onClick={() => setShowLeaderboard(true)} style={{ padding:"8px 14px", fontSize:10, fontFamily:"monospace", letterSpacing:2, background:"transparent", color:"#2ECC71", border:"1px solid #2ECC7160", cursor:"pointer" }}>🏆</button>
            <button className="hov-btn" onClick={() => setSyncOpen(true)} style={{ padding:"8px 14px", fontSize:10, fontFamily:"monospace", letterSpacing:2, background:"transparent", color:accent, border:`1px solid ${accent}40`, cursor:"pointer" }}>📱</button>
            <button className="hov-btn" onClick={() => adminMode ? setView("admin") : setShowAdminLogin(true)} style={{ padding:"8px 14px", fontSize:10, fontFamily:"monospace", letterSpacing:2, background:view==="admin"?accent:"transparent", color:view==="admin"?B.black:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer" }}>🔐</button>
          </div>
        </div>
      </div>

      {/* ── MAP VIEW ── */}
      {view === "map" && (
        <div style={{ padding: "32px 20px", maxWidth: 1200, margin: "0 auto" }}>

          {/* Progress Dashboard */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:28 }}>
            {[
              { label:"PROGRESS", val:`${progress}%`, col:accent },
              { label:"🔥 STREAK", val:`${streakData.streak}d`, col:"#FF6B35" },
              { label:"QUIZZES", val:`${Object.values(quizPassed).filter(Boolean).length}`, col:"#2ECC71" },
              { label:"DAYS ACTIVE", val:`${streakData.totalDays}`, col:"#4A9EFF" },
            ].map(stat => (
              <div key={stat.label} style={{ padding:"12px 14px", background:B.blackCard||"#0C0C0C", border:`1px solid ${B.blackBorder}`, textAlign:"center" }}>
                <div style={{ fontSize:20, fontWeight:900, color:stat.col, fontFamily:"monospace", lineHeight:1.1 }}>{stat.val}</div>
                <div style={{ fontSize:7, color:B.whiteMuted, letterSpacing:2, fontFamily:"monospace", marginTop:4 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 11, color: accent, fontFamily: "monospace", letterSpacing: 3, marginBottom: 8 }}>▣ THE 24/7 TRADER'S EDGE</div>
            <div style={{ fontSize: 15, color: B.whiteGray, lineHeight: 1.7, maxWidth: 800 }}>
              This track teaches you to trade Gold, Oil, BTC, and traditional futures across <span style={{ color: accent }}>three different execution wrappers</span>: broker CFDs on MT5, tokenized spot (XAUT), and perpetual futures on Binance, Bybit, and Hyperliquid. The goal isn't to pick one — it's to know when to use each.
            </div>
          </div>

          <div style={{ display: "grid", gap: 20 }}>
            {track.phases.map((phase, pIdx) => (
              <div key={phase.id} style={{
                background: B.blackCard, border: `1px solid ${B.blackBorder}`,
                padding: 24, borderRadius: 2,
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
                  <div style={{
                    minWidth: 44, height: 44, borderRadius: 2,
                    background: track.faint, border: `1px solid ${accent}60`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, color: accent, fontFamily: "monospace", fontWeight: "bold",
                  }}>{phase.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                      <div style={{ fontSize: 17, color: B.white }}>{phase.label}</div>
                      <Tag text={phase.weeks} color={accent} />
                    </div>
                    <div style={{ fontSize: 13, color: B.whiteGray, lineHeight: 1.6 }}>{phase.desc}</div>
                  </div>
                </div>

                <div style={{ display: "grid", gap: 8, marginLeft: 60 }}>
                  {phase.modules.map((mod, mIdx) => (
                    <button
                      key={mod.id}
                      className="hov-btn"
                      onClick={() => openLesson(mod)}
                      style={{
                        background: B.blackPanel, border: `1px solid ${B.blackBorder}`,
                        padding: "12px 16px", textAlign: "left", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                        <div style={{ fontSize: 9, color: B.whiteMuted, fontFamily: "monospace", minWidth: 28 }}>
                          {String(mIdx + 1).padStart(2, "0")}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, color: B.whiteOff }}>{mod.title}</div>
                          <div style={{ fontSize: 9, color: B.whiteMuted, fontFamily: "monospace", marginTop: 2, letterSpacing: 1 }}>
                            {mod.duration} · {mod.tag}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: 16, color: accent }}>→</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── LESSON VIEW ── */}
      {view === "lesson" && currentModule && (
        <div style={{ padding: "32px 20px", maxWidth: 900, margin: "0 auto" }}>
          <button onClick={() => setView("map")} className="hov-btn" style={{
            marginBottom: 20, padding: "6px 12px", fontSize: 10, fontFamily: "monospace", letterSpacing: 2,
            background: "transparent", color: B.whiteMuted, border: `1px solid ${B.blackBorder}`, cursor: "pointer",
          }}>← BACK TO MAP</button>

          <div style={{ marginBottom: 20 }}>
            <Tag text={currentModule.tag} color={accent} />
            <div style={{ fontSize: 26, color: B.white, marginTop: 12, lineHeight: 1.3 }}>{currentModule.title}</div>
            <div style={{ fontSize: 10, color: B.whiteMuted, fontFamily: "monospace", letterSpacing: 2, marginTop: 8 }}>
              DURATION · {currentModule.duration}
            </div>
          </div>

          <div style={{ background: B.blackCard, border: `1px solid ${B.blackBorder}`, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 10, color: accent, fontFamily: "monospace", letterSpacing: 2, marginBottom: 12 }}>▸ SUMMARY</div>
            <div style={{ fontSize: 14, color: B.whiteOff, lineHeight: 1.8 }}>{currentModule.summary}</div>
          </div>

          <div style={{ background: B.blackCard, border: `1px solid ${B.blackBorder}`, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 10, color: accent, fontFamily: "monospace", letterSpacing: 2, marginBottom: 12 }}>▸ KEY POINTS</div>
            <div style={{ display: "grid", gap: 10 }}>
              {currentModule.keyPoints.map((kp, i) => (
                <div key={i} style={{ display: "flex", gap: 12 }}>
                  <div style={{ color: accent, fontFamily: "monospace", fontSize: 11, minWidth: 22 }}>0{i + 1}</div>
                  <div style={{ fontSize: 13, color: B.whiteOff, lineHeight: 1.7 }}>{kp}</div>
                </div>
              ))}
            </div>
          </div>

          {currentModule.practice && (
            <div style={{ background: track.faint, border: `1px solid ${accent}40`, padding: 24, marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: accent, fontFamily: "monospace", letterSpacing: 2, marginBottom: 12 }}>▸ PRACTICE</div>
              <div style={{ fontSize: 13, color: B.whiteOff, lineHeight: 1.7 }}>{currentModule.practice}</div>
            </div>
          )}

          {currentModule.resources && currentModule.resources.length > 0 && (
            <div style={{ background: B.blackCard, border: `1px solid ${B.blackBorder}`, padding: 24 }}>
              <div style={{ fontSize: 10, color: accent, fontFamily: "monospace", letterSpacing: 2, marginBottom: 12 }}>▸ RESOURCES</div>
              <div style={{ display: "grid", gap: 8 }}>
                {currentModule.resources.map((r, i) => (
                  <a key={i} href={r.url} target="_blank" rel="noreferrer" style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                    padding: "12px 14px", background: B.blackPanel,
                    border: `1px solid ${B.blackBorder}`, textDecoration: "none",
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: B.whiteOff }}>{r.label}</div>
                      <div style={{ fontSize: 9, color: B.whiteMuted, fontFamily: "monospace", letterSpacing: 1, marginTop: 3 }}>
                        {r.type} · {r.tag}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, color: accent }}>↗</div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Video Embed */}
          {videoUrls[currentModule.id] && getYTEmbed(videoUrls[currentModule.id]) && (
            <div style={{ marginBottom:20, marginTop:20 }}>
              <div style={{ fontSize:10, color:accent, fontFamily:"monospace", letterSpacing:2, marginBottom:10 }}>▸ VIDEO LESSON</div>
              <div style={{ position:"relative", paddingBottom:"56.25%", height:0, overflow:"hidden" }}>
                <iframe src={getYTEmbed(videoUrls[currentModule.id])} title="Lesson Video"
                  style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", border:"none" }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </div>
          )}

          {/* SageMaster Callout */}
          <div style={{ padding:"14px 18px", marginBottom:20, marginTop:20, background:"#0A1628", border:"1px solid #4A9EFF30", borderLeft:"3px solid #4A9EFF" }}>
            <div style={{ fontSize:10, color:"#4A9EFF", letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>⚡ SAGEMASTER AI TOOLS FOR THIS LESSON</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {[{name:"DeepSage",href:"https://sagemaster.com/product/deepsage"},{name:"AI Grid",href:"https://sagemaster.com/product/ai-grid"},{name:"Helix",href:"https://sagemaster.com/product/helix"},{name:"AI DCA",href:"https://sagemaster.com/product/ai-dca"}].map(t => (
                <a key={t.name} href={t.href} target="_blank" rel="noopener noreferrer" style={{ padding:"5px 10px", fontSize:9, letterSpacing:1, fontFamily:"monospace", color:"#4A9EFF", border:"1px solid #4A9EFF40", textDecoration:"none", background:"#4A9EFF08" }}>{t.name} ↗</a>
              ))}
            </div>
          </div>

          {/* Complete Button */}
          <button className="hov-btn" onClick={() => toggleDone(currentModule.id)} style={{ marginBottom:20, padding:"9px 20px", fontSize:10, letterSpacing:2, fontFamily:"monospace", fontWeight:700, background:completed[currentModule.id]?`linear-gradient(135deg,${accent},${B.goldDark||"#9A7A10"})`:"transparent", color:completed[currentModule.id]?B.black:accent, border:`1px solid ${accent}`, cursor:"pointer" }}>
            {completed[currentModule.id] ? "✓ LESSON COMPLETE" : "MARK AS COMPLETE"}
          </button>

          {/* Notes */}
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:10, color:accent, fontFamily:"monospace", letterSpacing:2, marginBottom:8 }}>▸ YOUR NOTES</div>
            <textarea value={noteInput} onChange={e => setNoteInput(e.target.value)} placeholder="Your insights and observations..."
              style={{ width:"100%", minHeight:90, padding:12, background:"#0C0C0C", border:`1px solid ${B.blackBorder}`, color:B.whiteOff, fontSize:12, fontFamily:"Georgia,serif", resize:"vertical", outline:"none", lineHeight:1.7 }} />
            <button className="hov-btn" onClick={saveNote} style={{ marginTop:6, padding:"6px 14px", fontSize:9, letterSpacing:2, fontFamily:"monospace", background:"transparent", color:accent, border:`1px solid ${accent}50`, cursor:"pointer" }}>SAVE NOTES</button>
          </div>

          {/* Community Thread */}
          <LessonThread moduleId={currentModule.id} />

          {/* Quiz Prompt */}
          {MODULE_QUIZZES[currentModule.id] && !quizPassed[currentModule.id] && (
            <div style={{ padding:"14px 18px", marginBottom:20, background:"rgba(201,162,39,0.06)", border:"1px solid rgba(201,162,39,0.3)", borderLeft:"3px solid #C9A227", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:12, color:B.whiteOff }}>Test what you learned — pass to earn certificate</div>
              <button className="hov-btn" onClick={() => startQuiz(currentModule.id, currentModule.title)} style={{ padding:"7px 14px", fontSize:9, letterSpacing:2, fontFamily:"monospace", background:"#C9A227", color:B.black, border:"none", cursor:"pointer" }}>TAKE QUIZ →</button>
            </div>
          )}
          {quizPassed[currentModule.id] && (
            <div style={{ padding:"12px 18px", marginBottom:20, background:"#0A2010", border:"1px solid #2ECC7130", borderLeft:"3px solid #2ECC71", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:12, color:"#2ECC71" }}>✓ Quiz passed</div>
              <button className="hov-btn" onClick={() => generateCert(lbName||"Trader", currentModule.title, "COMMODITIES TRADING")} style={{ padding:"6px 12px", fontSize:9, letterSpacing:2, fontFamily:"monospace", background:"transparent", color:"#2ECC71", border:"1px solid #2ECC7150", cursor:"pointer" }}>VIEW CERT</button>
            </div>
          )}
        </div>
      )}

      {/* ── CREDIT VIEW ── */}
      {view === "credit" && (
        <div style={{ padding: "40px 20px", maxWidth: 800, margin: "0 auto" }}>
          <div style={{ fontSize: 11, color: accent, fontFamily: "monospace", letterSpacing: 3, marginBottom: 12 }}>▣ TRACK ACKNOWLEDGEMENTS</div>
          <div style={{ fontSize: 24, color: B.white, marginBottom: 20 }}>CFD · Futures · Commodities Track</div>
          <div style={{ fontSize: 14, color: B.whiteGray, lineHeight: 1.8, marginBottom: 20 }}>{track.credit}</div>

          <div style={{ background: B.blackCard, border: `1px solid ${B.blackBorder}`, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 10, color: accent, fontFamily: "monospace", letterSpacing: 2, marginBottom: 10 }}>▸ SAGEMASTER TOOLS REFERENCED</div>
            <div style={{ fontSize: 13, color: B.whiteOff, lineHeight: 1.8 }}>
              DeepSage (sentiment) · AI Grid (range markets) · Helix (24/7 automation) · Intelligent Backtest (futures module) · Magic Terminal
            </div>
          </div>

          <div style={{ background: B.blackCard, border: `1px solid ${B.blackBorder}`, padding: 20 }}>
            <div style={{ fontSize: 10, color: accent, fontFamily: "monospace", letterSpacing: 2, marginBottom: 10 }}>▸ IMPORTANT DISCLAIMER</div>
            <div style={{ fontSize: 12, color: B.whiteGray, lineHeight: 1.7 }}>
              This content is for educational purposes only and does not constitute financial advice. Leveraged trading in CFDs, futures, and perpetuals carries significant risk of loss. Past performance does not guarantee future results. Consult a licensed financial advisor before trading. FLEXX Academy is not responsible for any trading losses.
            </div>
          </div>

          <div style={{ marginTop: 24 }}>
            <button onClick={() => setView("map")} className="hov-btn" style={{
              padding: "8px 14px", fontSize: 10, fontFamily: "monospace", letterSpacing: 2,
              background: "transparent", color: B.whiteMuted, border: `1px solid ${B.blackBorder}`, cursor: "pointer",
            }}>← BACK TO COURSE</button>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <div style={{ borderTop: `1px solid ${B.blackBorder}`, marginTop: 48 }}>
        <GoldBar color={accent} />
        <div style={{ padding: "14px 20px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 8, color: B.whiteMuted, fontFamily: "monospace", letterSpacing: 2 }}>FLEXX ACADEMY · DUBAI · 2026</div>
          <div style={{ fontSize: 8, color: B.whiteMuted, fontFamily: "monospace", letterSpacing: 2 }}>CFD · FUTURES · COMMODITIES · PERPETUALS</div>
        </div>
      </div>
    </div>
  );
}
